import { ApiService } from "@/lib/api"
import type {
  GroupAvailabilityResponse,
  GroupMemberInput,
  PaymentMethod,
} from "@/lib/types/appointment.types"
import type { AppointmentStore, GroupMemberDraft } from "@/store/appointment-store"
import { openPaystackPopupFromInitiate } from "@/lib/payments/paystack-handler"
import { wallClockToIso } from "@/lib/utils/business-timezone"

export function memberDuration(member: GroupMemberDraft): number {
  return member.services.reduce((sum, s) => sum + (Number(s.duration) || 0), 0)
}

export function memberTotal(member: GroupMemberDraft): number {
  return member.services.reduce((sum, s) => sum + (Number(s.price) || 0), 0)
}

function addMinutesToTime(time: string, minutes: number): string {
  const match = time.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return time
  const total = parseInt(match[1], 10) * 60 + parseInt(match[2], 10) + minutes
  const h = Math.floor(total / 60) % 24
  const m = total % 60
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`
}

function buildMemberInputs(store: AppointmentStore): GroupMemberInput[] {
  const timezone = store.businessTimezone
  const date = store.groupDate
  const isCustom = store.groupIsCustomBooking

  return store.groupMembers.map((member) => {
    const name = member.beneficiaryName.trim()
    const input: GroupMemberInput = {
      beneficiaryName: name,
      // The API requires notes on every seat; fall back to the name when the
      // customer left the field blank.
      beneficiaryNotes: member.beneficiaryNotes.trim() || name,
      beneficiaryPhone: member.beneficiaryPhone.trim() || undefined,
      beneficiaryEmail: member.beneficiaryEmail.trim() || undefined,
      serviceIds: member.services
        .map((s) => Number(s.id))
        .filter((id) => Number.isFinite(id) && id > 0),
      isOrganizerSeat: member.isOrganizerSeat,
    }
    if (!isCustom && date && member.startTime) {
      const endClock = addMinutesToTime(member.startTime, memberDuration(member))
      input.startTime = wallClockToIso(date, member.startTime, timezone)
      input.endTime = wallClockToIso(date, endClock, timezone)
    }
    return input
  })
}

/** Fallback day so the API always receives a valid date on custom requests. */
function resolveDate(store: AppointmentStore): string {
  return store.groupDate || new Date().toISOString().slice(0, 10)
}

export type GroupStepValidation = { valid: boolean; message?: string }

export function validateGuests(store: AppointmentStore): GroupStepValidation {
  if (store.groupMembers.length === 0) {
    return { valid: false, message: "Add at least one guest to continue." }
  }
  const unnamed = store.groupMembers.some((m) => !m.beneficiaryName.trim())
  if (unnamed) {
    return { valid: false, message: "Give every guest a name to continue." }
  }
  return { valid: true }
}

export function validateGroupServices(store: AppointmentStore): GroupStepValidation {
  const empty = store.groupMembers.filter((m) => m.services.length === 0)
  if (empty.length > 0) {
    return {
      valid: false,
      message: `Pick at least one service for ${empty
        .map((m) => m.beneficiaryName.trim() || "your guest")
        .join(", ")}.`,
    }
  }
  return { valid: true }
}

export function validateGroupTimes(store: AppointmentStore): GroupStepValidation {
  if (store.groupIsCustomBooking) return { valid: true }
  if (!store.groupDate) {
    return { valid: false, message: "Pick a day for the party." }
  }
  const missing = store.groupMembers.filter((m) => !m.startTime)
  if (missing.length > 0) {
    return {
      valid: false,
      message: `Pick a start time for ${missing
        .map((m) => m.beneficiaryName.trim() || "your guest")
        .join(", ")}.`,
    }
  }
  return { valid: true }
}

export async function checkGroupAvailability(
  store: AppointmentStore,
  organizerUserId?: number,
): Promise<GroupAvailabilityResponse> {
  const result = await ApiService.checkGroupAvailability({
    organizerUserId,
    appointmentDate: resolveDate(store),
    members: buildMemberInputs(store),
  })
  store.setGroupAvailability(result)
  return result
}

export type SubmitGroupBookingArgs = {
  store: AppointmentStore
  organizerUserId: number
  organizerEmail?: string
}

/**
 * Creates the booking group, running the card payment flow first so the group
 * is only persisted against a real payment reference.
 */
export async function submitGroupBooking({
  store,
  organizerUserId,
  organizerEmail,
}: SubmitGroupBookingArgs): Promise<void> {
  const isCustom = store.groupIsCustomBooking
  const paymentMethod: PaymentMethod = store.groupPaymentMethod
  const members = buildMemberInputs(store)
  const notes = store.groupNotes.trim() || undefined

  if (!isCustom && paymentMethod === "Card") {
    if (!organizerEmail) {
      throw new Error("Email is required for card payment.")
    }
    const initiated = await ApiService.initiateCardPayment({
      customerEmail: organizerEmail,
      serviceIds: store.groupMembers.flatMap((m) =>
        m.services.map((s) => Number(s.id)).filter((id) => Number.isFinite(id) && id > 0),
      ),
      discountCode: store.discountCode?.trim() || undefined,
      paymentPurpose: "full",
    })

    await ApiService.createGroupAppointment({
      organizerUserId,
      appointmentDate: resolveDate(store),
      members,
      paymentMethod: "Card",
      paymentReference: initiated.paymentReference,
      discountCode: store.discountCode?.trim() || undefined,
      notes,
      acceptTerms: true,
      notificationsConsent: store.notificationsConsent,
    })

    if (initiated.walletOnly) return

    const settings = await ApiService.getPublicPaymentSettings()
    const publicKey =
      settings.paystackPublicKey?.trim() ||
      process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY?.trim()
    if (!publicKey) {
      throw new Error("Paystack is not configured.")
    }
    const paystackResult = await openPaystackPopupFromInitiate(
      publicKey,
      initiated,
      organizerEmail,
    )
    await ApiService.verifyPayment(paystackResult.reference || initiated.paymentReference)
    return
  }

  await ApiService.createGroupAppointment({
    organizerUserId,
    appointmentDate: resolveDate(store),
    members,
    paymentMethod: isCustom ? undefined : paymentMethod,
    isCustomBooking: isCustom,
    discountCode: store.discountCode?.trim() || undefined,
    notes,
    acceptTerms: true,
    notificationsConsent: store.notificationsConsent,
  })
}
